import { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import logo from '../assets/wonderstood-high-resolution-logo-grayscale-transparent.png';
import EmailVerification from './RegistrationPages/EmailVerification';
import { signUp } from '../lib/auth';
import { createUserDocument } from '../lib/userService';
import { CreateUserData } from '../types/user';

const SWE_SPECIALIZATIONS = [
  'Frontend Engineer',
  'Backend Engineer',
  'Full Stack Engineer',
  'iOS Developer',
  'Android Developer',
  'Mobile Developer',
  'ML Engineer',
  'Data Engineer',
  'DevOps Engineer',
  'Security Engineer',
  'Platform Engineer',
  'Cloud Engineer',
];

interface RegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialUserType?: 'client' | 'professional';
  onSwitchToSignIn: () => void;
}

export default function RegistrationModal({ isOpen, onClose, initialUserType, onSwitchToSignIn }: RegistrationModalProps) {
  const [showVerification, setShowVerification] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userType, setUserType] = useState<'client' | 'professional'>(initialUserType || 'client');
  const [selectedSpecialization, setSelectedSpecialization] = useState('');
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Sync userType with parent's initialUserType when modal opens
  useEffect(() => {
    if (isOpen && initialUserType) {
      setUserType(initialUserType);
    }
  }, [isOpen, initialUserType]);

  const handleClose = () => {
    setShowVerification(false);
    setUserEmail('');
    setUserType(initialUserType || 'client');
    setSelectedSpecialization('');
    setFormData({ email: '', password: '' });
    setShowPassword(false);
    setTermsAccepted(false);
    setIsLoading(false);
    setError('');
    onClose();
  };

  const sanitizeInput = (value: string, type: 'email' | 'password'): string => {
    let sanitized = value.replace(/[\x00-\x1F\x7F]/g, '');
    sanitized = sanitized.replace(/<[^>]*>/g, '');
    sanitized = sanitized.replace(/&[#\w]+;/g, '');

    if (type === 'email') {
      sanitized = sanitized.replace(/\s+/g, '').toLowerCase();
      sanitized = sanitized.slice(0, 254);
      sanitized = sanitized.replace(/[^a-z0-9@._+-]/g, '');
    } else if (type === 'password') {
      sanitized = sanitized.trim();
      sanitized = sanitized.slice(0, 128);
      const dangerousPatterns = [/javascript:/i, /<script/i, /onload=/i, /onerror=/i, /onclick=/i, /eval\(/i, /expression\(/i];
      for (const pattern of dangerousPatterns) {
        if (pattern.test(sanitized)) return '';
      }
    }
    return sanitized;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: sanitizeInput(value, name as 'email' | 'password') }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (!termsAccepted) throw new Error('You must accept the Terms of Service to create an account');

      // Email validation
      const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
      if (!formData.email || formData.email.length < 5 || formData.email.length > 254) throw new Error('Please enter a valid email address');
      if (!emailRegex.test(formData.email)) throw new Error('Please enter a valid email address');
      const suspiciousPatterns = [/\.{2,}/, /@{2,}/, /^\./, /\.$/, /@\./, /\.@/];
      for (const pattern of suspiciousPatterns) {
        if (pattern.test(formData.email)) throw new Error('Invalid email format');
      }

      // Password validation
      if (!formData.password || formData.password.length < 8) throw new Error('Password must be at least 8 characters');
      if (formData.password.length > 128) throw new Error('Password must be less than 128 characters');
      if (!/[A-Z]/.test(formData.password)) throw new Error('Password must contain at least 1 uppercase letter');
      if (!/[a-z]/.test(formData.password)) throw new Error('Password must contain at least 1 lowercase letter');
      if (!/[0-9]/.test(formData.password)) throw new Error('Password must contain at least 1 number');
      const commonPasswords = ['password', '12345678', 'qwerty', 'abc123', 'password123', 'admin', 'letmein', 'welcome', 'monkey', '1234567890'];
      if (commonPasswords.includes(formData.password.toLowerCase())) throw new Error('Password is too common. Please choose a more secure password');
      if (/(.)\1{3,}/.test(formData.password)) throw new Error('Password cannot contain more than 3 repeated characters in a row');

      // Specialization validation for engineers
      if (userType === 'professional' && !selectedSpecialization) throw new Error('Please select your specialization');

      const userCredential = await signUp(formData.email, formData.password, formData.email);

      const userData: CreateUserData = {
        firstName: '',
        lastName: '',
        email: formData.email,
        country: 'United States',
        userType,
        ...(userType === 'professional' && selectedSpecialization ? {
          title: selectedSpecialization,
          category: 'Technology & Software',
        } : {}),
        notifications: {
          bookings: true,
          ratings: true,
          payments: true,
          cancellations: true,
        },
      };

      await createUserDocument(userCredential.user, userData);

      setUserEmail(formData.email);
      setShowVerification(true);
    } catch (error: any) {
      if (error.code === 'auth/email-already-in-use') {
        setError('This email is already registered. Please sign in or use a different email.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address format.');
      } else if (error.code === 'auth/weak-password') {
        setError('Password is too weak. Please choose a stronger password.');
      } else if (error.code === 'auth/network-request-failed') {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(error.message || 'An error occurred during registration');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/70 backdrop-blur-md" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-400" enterFrom="opacity-0 scale-95 translate-y-2" enterTo="opacity-100 scale-100 translate-y-0" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl liquid-glass shadow-2xl shadow-black/50 transition-all flex flex-col">
                {/* Header */}
                <div className="relative px-5 sm:px-6 py-4 flex-shrink-0 border-b border-white/10">
                  <div className="flex items-center justify-between">
                    <img src={logo} alt="Wonderstood" className="h-5 sm:h-6 w-auto brightness-0 invert opacity-70" />
                    <button onClick={handleClose} className="rounded-full p-1.5 text-white/40 hover:text-white hover:bg-white/10 transition-all duration-200">
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 flex flex-col overflow-y-auto p-5 sm:p-7">
                  {showVerification ? (
                    <EmailVerification email={userEmail} />
                  ) : (
                    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                      {/* Title */}
                      <div>
                        <h2 className="text-2xl sm:text-3xl font-medium tracking-tight text-white">
                          Create your <span className="font-serif italic">account</span>
                        </h2>
                        <p className="mt-1.5 text-sm text-white/50">Join the community in under a minute.</p>
                      </div>

                      {/* User type toggle */}
                      <div>
                        <label className="block text-xs font-medium text-white/40 uppercase tracking-wide mb-2.5">I am a...</label>
                        <div className="flex rounded-xl bg-white/5 border border-white/10 p-1">
                          <button
                            type="button"
                            onClick={() => { setUserType('client'); setSelectedSpecialization(''); }}
                            className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-semibold transition-all duration-200 ${
                              userType === 'client'
                                ? 'bg-white text-black shadow-sm'
                                : 'text-white/50 hover:text-white'
                            }`}
                          >
                            Student
                          </button>
                          <button
                            type="button"
                            onClick={() => setUserType('professional')}
                            className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-semibold transition-all duration-200 ${
                              userType === 'professional'
                                ? 'bg-white text-black shadow-sm'
                                : 'text-white/50 hover:text-white'
                            }`}
                          >
                            Software Engineer
                          </button>
                        </div>
                      </div>

                      {/* Specialization chips — engineers only */}
                      {userType === 'professional' && (
                        <div>
                          <label className="block text-xs font-medium text-white/40 uppercase tracking-wide mb-2.5">Your specialization</label>
                          <div className="flex flex-wrap gap-2">
                            {SWE_SPECIALIZATIONS.map((spec) => (
                              <button
                                key={spec}
                                type="button"
                                onClick={() => setSelectedSpecialization(spec === selectedSpecialization ? '' : spec)}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${
                                  selectedSpecialization === spec
                                    ? 'bg-white text-black border-white shadow-sm'
                                    : 'bg-transparent border-white/15 text-white/50 hover:border-white/30 hover:text-white'
                                }`}
                              >
                                {spec}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Email */}
                      <div>
                        <label htmlFor="reg-email" className="block text-sm font-medium text-white/80 mb-1.5">Email</label>
                        <input
                          id="reg-email"
                          name="email"
                          type="email"
                          placeholder="name@email.com"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          autoComplete="email"
                          spellCheck="false"
                          className="block w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-white/30 focus:ring-1 focus:ring-white/20 focus:outline-none transition-all"
                        />
                      </div>

                      {/* Password */}
                      <div>
                        <label htmlFor="reg-password" className="block text-sm font-medium text-white/80 mb-1.5">Password</label>
                        <div className="relative">
                          <input
                            id="reg-password"
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="At least 8 characters"
                            value={formData.password}
                            onChange={handleInputChange}
                            required
                            autoComplete="new-password"
                            spellCheck="false"
                            className="block w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 pr-11 text-sm text-white placeholder:text-white/30 focus:border-white/30 focus:ring-1 focus:ring-white/20 focus:outline-none transition-all"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-white/30 hover:text-white/60 transition-colors"
                          >
                            {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>

                      {/* Terms */}
                      <div className="flex items-start gap-3">
                        <input
                          id="terms"
                          type="checkbox"
                          checked={termsAccepted}
                          onChange={(e) => setTermsAccepted(e.target.checked)}
                          className="mt-0.5 h-4 w-4 rounded bg-white/5 border-white/20 text-white focus:ring-white/20 focus:ring-1 cursor-pointer accent-white"
                        />
                        <label htmlFor="terms" className="text-xs text-white/40 cursor-pointer leading-relaxed">
                          I agree to the{' '}
                          <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white underline underline-offset-2">Terms of Service</a>
                          {' '}and{' '}
                          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white underline underline-offset-2">Privacy Policy</a>
                        </label>
                      </div>

                      {/* Error */}
                      {error && (
                        <div className="rounded-xl bg-red-500/10 p-3.5 border border-red-500/20">
                          <p className="text-sm text-red-300">{error}</p>
                        </div>
                      )}

                      {/* Submit */}
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full rounded-full bg-white text-black py-3 text-sm font-semibold hover:scale-[1.02] hover:shadow-lg hover:shadow-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                      >
                        {isLoading ? 'Creating account...' : userType === 'professional' ? 'Join as an engineer' : 'Create account'}
                      </button>

                      {/* Sign in link */}
                      <p className="text-center text-sm text-white/40">
                        Already have an account?{' '}
                        <button
                          type="button"
                          onClick={() => { handleClose(); onSwitchToSignIn(); }}
                          className="text-white/70 hover:text-white font-medium transition-colors"
                        >
                          Sign in
                        </button>
                      </p>
                    </form>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
